import { CheckCircleIcon, type IconProps, InfoCircleIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { DialogHeaderStatus, DialogHeaderVariant } from './types';

const iconClasses = cva({
  base: 'p-6 h-[48px] w-[48px] aspect-square border-none rounded-full',
  variants: {
    status: {
      info: 'bg-brand-100 text-brand-600',
      error: 'bg-error-100 text-error-600',
      success: 'bg-success-100 text-success-600',
      warning: 'bg-warning-100 text-warning-600',
      neutral: 'bg-gray-utility-100 text-gray-utility-500',
    },
  },
});

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The variant of the dialog header
   * @type {DialogHeaderVariant}
   * @optional
   */
  variant?: DialogHeaderVariant;
  /**
   * The status of the dialog header
   * @type {DialogHeaderStatus}
   * @optional
   */
  status?: DialogHeaderStatus;
  /**
   * Custom icon to display
   * @type {React.ComponentType<IconProps>}
   * @optional
   */
  icon?: React.ComponentType<IconProps>;
}

function DialogHeader(props: DialogHeaderProps) {
  const {
    children,
    className,
    variant = DialogHeaderVariant.Default,
    status = DialogHeaderStatus.Warning,
    icon: Icon,
    ...other
  } = props;

  const icon = React.useMemo(
    () =>
      Icon ? (
        <Icon aria-hidden={true} size={24} focusable={false} />
      ) : (
        {
          [DialogHeaderStatus.Info]: <InfoCircleIcon aria-hidden={true} size={24} focusable={false} />,
          [DialogHeaderStatus.Error]: <InfoCircleIcon aria-hidden={true} size={24} focusable={false} />,
          [DialogHeaderStatus.Success]: <CheckCircleIcon aria-hidden={true} size={24} focusable={false} />,
          [DialogHeaderStatus.Warning]: <InfoCircleIcon aria-hidden={true} size={24} focusable={false} />,
          [DialogHeaderStatus.Neutral]: <InfoCircleIcon aria-hidden={true} size={24} focusable={false} />,
        }[status]
      ),
    [status, Icon],
  );

  return (
    <div
      className={cx('flex flex-col px-12 pt-12 gap-4', variant === DialogHeaderVariant.Icon && 'gap-8', className)}
      {...other}
    >
      {variant === DialogHeaderVariant.Icon && <div className={iconClasses({ status })}>{icon}</div>}
      {children}
    </div>
  );
}

export { DialogHeader };
